import axios from 'axios';
import {
  ensureScormLmsLink,
  installScormSessionLifecycle,
  markScormCourseEngaged,
  relayScormProgressToLms,
  syncScormProgressToLms,
} from '../utils/scormLifecycle';
import {
  isRestrictedDownloadEnvironment,
  openDownloadUrl,
  saveBlobAsFile,
} from '../utils/download';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
/** Délai HTTP (ms). Les cours lourds ou un backend lent au démarrage peuvent dépasser 10s. */
const API_TIMEOUT_MS = Number(process.env.REACT_APP_API_TIMEOUT_MS) || 60000;
const SCORM_SESSION_KEY = 'student_scorm_session';

interface ScormSessionData {
  token: string;
  lmsUserId: string;
}

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT_MS,
});

const setScormSession = (session: ScormSessionData): void => {
  localStorage.setItem(SCORM_SESSION_KEY, JSON.stringify(session));
  installScormSessionLifecycle();
};

const clearScormSession = (): void => {
  localStorage.removeItem(SCORM_SESSION_KEY);
  sessionStorage.removeItem('student_scorm_last_relay');
};

const relayScormCompletion = async (
  completionStatus: 'incomplete' | 'completed',
  details: string,
  progressPercent?: number
): Promise<void> => {
  await relayScormProgressToLms(completionStatus, details, progressPercent);
};

const getCourseProgressPercent = async (courseId: string): Promise<number | null> => {
  try {
    const response = await api.get(`/student/courses/${courseId}`, {
      params: { compact: '1' },
    });
    const progress = Number(response.data?.progress);
    if (Number.isNaN(progress)) {
      return null;
    }
    return Math.max(0, Math.min(100, progress));
  } catch {
    return null;
  }
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('student_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Avoid ngrok free plan HTML splash page.
    config.headers['ngrok-skip-browser-warning'] = '1';
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't logout on 403 errors from quiz retry - these are expected business logic errors
    const isQuizRetryError = error.config?.url?.includes('/quizzes/response/chapter/') && 
                             error.config?.method === 'delete' &&
                             error.response?.status === 403;

    /** Init SCORM / code : 401 métier (export désactivé, token invalide…) — pas de purge session ni redirection sans query LMS */
    const requestPath = String(error.config?.url || '');
    const isScormSessionRequest = requestPath.includes('/scorm/session');

    // Formation verrouillée (date de déblocage groupe dans le futur) : erreur métier, pas une
    // session invalide — ne pas déconnecter, laisser la page afficher l'écran dédié.
    const isCourseLocked =
      error.response?.status === 403 && error.response?.data?.code === 'COURSE_LOCKED';

    if (
      !isScormSessionRequest &&
      !isCourseLocked &&
      (error.response?.status === 401 || (error.response?.status === 403 && !isQuizRetryError))
    ) {
      // Clear all authentication data
      localStorage.removeItem('student_token');
      localStorage.removeItem('student_user');
      console.log('🔐 Session expirée ou invalide, déconnexion...');
      
      // Only redirect if we're not already on the login page
      if (window.location.pathname !== '/login') {
        const qs = typeof window.location.search === 'string' ? window.location.search : '';
        window.location.replace(`/login${qs}`);
      }
    }
    return Promise.reject(error);
  }
);

// Auth API functions
export const authAPI = {
  setScormSession,
  clearScormSession,
  ensureScormLmsLink,
  syncScormProgressToLms,

  // Send verification code
  sendCode: async (email: string, language: string = 'fr') => {
    const response = await api.post('/auth-code/send-code', {
      email,
      language
    });
    return response.data;
  },

  // Verify code and login
  verifyCode: async (email: string, code: string, language: string = 'fr') => {
    const response = await api.post('/auth-code/verify-code', {
      email,
      code,
      language
    });
    clearScormSession();
    return response.data;
  },

  // Check if email exists
  checkEmail: async (email: string) => {
    const response = await api.post('/auth-code/check-email', {
      email
    });
    return response.data;
  },

  // Verify magic link
  verifyMagicLink: async (token: string) => {
    const response = await api.get(`/magic-link/verify/${token}`);
    clearScormSession();
    return response.data;
  },

  // Check magic link validity
  checkMagicLink: async (token: string) => {
    const response = await api.get(`/magic-link/check/${token}`);
    return response.data;
  },

  scormInitSession: async (payload: {
    token: string;
    lmsUserId: string;
    firstName: string;
    lastName: string;
    language?: string;
  }): Promise<{
    status: string;
    token?: string;
    user?: unknown;
    formationId?: string;
    flowId?: string;
    maskedEmail?: string;
    scormSettings?: { freeEnrollment: boolean; emailOptional: boolean };
    courseLanguages?: { availableLanguages: string[]; defaultLanguage: string };
  }> => {
    const response = await api.post('/scorm/session/init', payload);
    return response.data;
  },

  scormIdentifyByEmail: async (payload: {
    token: string;
    lmsUserId: string;
    email: string;
    language?: string;
  }) => {
    const response = await api.post('/scorm/session/identify-by-email', payload);
    return response.data;
  },

  scormVerifyCode: async (payload: {
    token: string;
    lmsUserId: string;
    code: string;
    flowId?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
  }) => {
    const response = await api.post('/scorm/session/verify-code', payload);
    return response.data;
  },

  scormFreeEnroll: async (payload: {
    token: string;
    lmsUserId: string;
    firstName: string;
    lastName: string;
    email?: string;
    code?: string;
    skipEmail?: boolean;
    language?: string;
  }) => {
    const response = await api.post('/scorm/session/free-enroll', payload);
    return response.data;
  }
};

// Course API functions
export type GetStudentCourseOptions = { full?: boolean };

/** CloudFront peut renvoyer index.html (200) à la place d'un JSON API (ex. 403 mappé en SPA). */
export function isHtmlApiPayload(data: unknown): boolean {
  if (typeof data !== 'string') return false;
  const s = data.trim().toLowerCase();
  return s.startsWith('<!doctype html') || (s.includes('<html') && s.includes('id="root"'));
}

export type CourseLockStatus = { locked: boolean; unlockDate: string | null };

/** Repli : statut de verrouillage depuis la liste des formations (endpoint fiable). */
export async function fetchCourseLockStatusFromList(courseId: string): Promise<CourseLockStatus> {
  try {
    const response = await api.get('/student/courses');
    const list = response.data;
    const courses = Array.isArray(list?.courses) ? list.courses : [];
    const match = courses.find((c: { id?: string }) => String(c.id) === String(courseId));
    if (!match) return { locked: false, unlockDate: null };
    return {
      locked: !!(match as { courseLocked?: boolean }).courseLocked,
      unlockDate: (match as { unlockDate?: string | null }).unlockDate ?? null,
    };
  } catch {
    return { locked: false, unlockDate: null };
  }
}

/** Par défaut : `compact=1` (léger). Passe `{ full: true }` pour modules + questions quiz. */
export async function fetchStudentCourse(
  courseId: string,
  options?: GetStudentCourseOptions
): Promise<any> {
  const params =
    options?.full === true
      ? undefined
      : { compact: '1' };
  const response = await api.get(`/student/courses/${courseId}`, { params });
  const data = response.data;
  if (isHtmlApiPayload(data)) {
    const err = new Error('API returned HTML instead of JSON') as Error & { code: string };
    err.code = 'API_HTML_RESPONSE';
    throw err;
  }
  return data;
}

export const courseAPI = {
  getCourses: async () => {
    const response = await api.get('/student/courses');
    return response.data;
  },

  getCourse: fetchStudentCourse,
};

// Certificate API functions
export const certificateAPI = {
  // Vérifie l'éligibilité au certificat de réalisation pour la formation
  getEligibility: async (courseId: string) => {
    const response = await api.get(`/certificates/course/${courseId}/eligibility`);
    return response.data as {
      eligible: boolean;
      courseType: 'elearning' | 'blended' | 'coaching' | null;
      reason: string | null;
      alreadyIssued: boolean;
    };
  },

  // Télécharge le certificat (PDF) et déclenche l'enregistrement dans le navigateur
  download: async (courseId: string, fileName = 'certificat.pdf') => {
    const downloadPath = `${API_BASE_URL}/certificates/course/${courseId}/download`;
    if (isRestrictedDownloadEnvironment()) {
      openDownloadUrl(downloadPath, { authenticate: true });
      return;
    }
    const response = await api.get(`/certificates/course/${courseId}/download`, {
      responseType: 'blob',
    });
    await saveBlobAsFile(new Blob([response.data], { type: 'application/pdf' }), fileName, 'application/pdf');
  },
};

export type QuizSubmitAnswer = {
  questionId?: string;
  questionOrder: number;
  selectedOptionId?: string;
  selectedAnswerIndex: number;
};

// Quiz API functions
export const quizAPI = {
  // Submit quiz response
  submitQuiz: async (chapterId: string, quizId: string, answers: QuizSubmitAnswer[]) => {
    const response = await api.post('/quizzes/submit', {
      chapterId,
      quizId,
      answers
    });
    return response.data;
  },

  // Get student's quiz response
  getQuizResponse: async (chapterId: string, quizId: string) => {
    const response = await api.get(`/quizzes/response/${chapterId}/${quizId}`);
    return response.data;
  },

  // Delete quiz response (for retry)
  deleteQuizResponse: async (chapterId: string, quizId: string) => {
    const response = await api.delete(`/quizzes/response/chapter/${chapterId}/quiz/${quizId}`);
    return response.data;
  }
};

// Student Notes API functions
export const notesAPI = {
  // Save or update a note (using capsuleId instead of capsuleOrder)
  saveNote: async (courseId: string, chapterId: string, capsuleId: string, content: string) => {
    const response = await api.post('/student/notes', {
      courseId,
      chapterId,
      capsuleId,
      content
    });
    return response.data;
  },

  // Get a note (using capsuleId instead of capsuleOrder)
  getNote: async (courseId: string, chapterId: string, capsuleId: string) => {
    const response = await api.get(`/student/notes/${courseId}/${chapterId}/${capsuleId}`);
    return response.data;
  },

  // Get all notes for a course
  getCourseNotes: async (courseId: string) => {
    const response = await api.get(`/student/notes/course/${courseId}`);
    return response.data;
  },

  // Delete a note
  deleteNote: async (noteId: string) => {
    const response = await api.delete(`/student/notes/${noteId}`);
    return response.data;
  }
};

// Submissions API functions (for question answers in capsules)
export const submissionAPI = {
  // Save or update a submission
  saveSubmission: async (courseId: string, chapterId: string, capsuleId: string, moduleId: string, answer: string) => {
    const response = await api.post('/submissions', {
      courseId,
      chapterId,
      capsuleId,
      moduleId,
      answer
    });
    return response.data;
  },

  // Get a specific submission
  getSubmission: async (courseId: string, chapterId: string, capsuleId: string, moduleId: string) => {
    const response = await api.get(`/submissions/${courseId}/${chapterId}/${capsuleId}/${moduleId}`);
    return response.data;
  },

  // Get all submissions for a capsule
  getCapsuleSubmissions: async (courseId: string, chapterId: string, capsuleId: string) => {
    const response = await api.get(`/submissions/${courseId}/${chapterId}/${capsuleId}`);
    return response.data;
  }
};

export const chapterRatingAPI = {
  saveRating: async (courseId: string, chapterId: string, rating: number, comment?: string) => {
    const response = await api.post('/chapter-ratings', {
      courseId,
      chapterId,
      rating,
      comment
    });
    return response.data;
  },
  getRating: async (courseId: string, chapterId: string) => {
    const response = await api.get(`/chapter-ratings/${courseId}/${chapterId}`);
    return response.data;
  }
};

export const workshopRatingAPI = {
  getPending: async (courseId: string) => {
    const response = await api.get(`/workshop-ratings/pending/${courseId}`);
    return response.data as { pending: PendingWorkshopRating[] };
  },
  save: async (payload: WorkshopRatingSavePayload) => {
    const response = await api.post('/workshop-ratings', payload);
    return response.data;
  }
};

export type StudentReminderPreferences = {
  reminderEmailsEnabled: boolean;
  email: string | null;
  emailEditable: boolean;
  isScormNoEmail: boolean;
};

export type CourseReminderPreferences = {
  courseId: string;
  reminderEmailsEnabled: boolean;
  globalReminderEmailsEnabled: boolean;
  effectiveReminderEmailsEnabled: boolean;
};

export const reminderPreferencesAPI = {
  getGlobal: async () => {
    const response = await api.get('/student/reminder-preferences');
    return response.data as StudentReminderPreferences;
  },
  updateGlobal: async (reminderEmailsEnabled: boolean) => {
    const response = await api.patch('/student/reminder-preferences', { reminderEmailsEnabled });
    return response.data as StudentReminderPreferences;
  },
  setScormEmail: async (email: string, emailConfirm: string) => {
    const response = await api.post('/student/email', { email, emailConfirm });
    return response.data as StudentReminderPreferences;
  },
  getCourse: async (courseId: string) => {
    const response = await api.get(`/student/courses/${courseId}/reminder-preferences`);
    return response.data as CourseReminderPreferences;
  },
  updateCourse: async (courseId: string, reminderEmailsEnabled: boolean) => {
    const response = await api.patch(`/student/courses/${courseId}/reminder-preferences`, {
      reminderEmailsEnabled,
    });
    return response.data as CourseReminderPreferences;
  },
};

export const resourceEmailAPI = {
  send: async (
    courseId: string,
    payload: {
      chapterId: string;
      itemId: string;
      moduleId: string;
      email?: string;
      emailConfirm?: string;
    }
  ) => {
    const response = await api.post(`/student/courses/${courseId}/resource-email`, payload);
    return response.data as { email: string; cooldownSeconds: number };
  },
};

export interface PendingWorkshopRating {
  courseId: string;
  chapterId: string;
  workshopItemId: string;
  chapterTitle: Record<string, string> | string;
  workshopTitle: Record<string, string> | string;
  workshopDate?: string | null;
  startTime?: string | null;
  endTime?: string | null;
}

export interface WorkshopRatingSavePayload {
  courseId: string;
  chapterId: string;
  workshopItemId: string;
  status: 'rated' | 'absent';
  rating?: number;
  comment?: string;
}

export const progressAPI = {
  markItemComplete: async (courseId: string, chapterId: string, itemType: 'capsule' | 'quiz' | 'workshop', itemId: string, itemOrder?: number) => {
    const response = await api.post('/progress/item/complete', {
      courseId,
      chapterId,
      itemType,
      itemId,
      itemOrder // Keep for backward compatibility
    });
    const courseProgress = await getCourseProgressPercent(courseId);
    const isCourseCompleted = courseProgress !== null && courseProgress >= 100;
    markScormCourseEngaged();
    await relayScormCompletion(
      isCourseCompleted ? 'completed' : 'incomplete',
      `Item completed: ${itemType}:${itemId}`,
      courseProgress ?? undefined
    );
    return response.data;
  },
  markModuleComplete: async (courseId: string, chapterId: string, capsuleOrder: number, moduleOrder: number) => {
    const response = await api.post('/progress/module/complete', {
      courseId,
      chapterId,
      capsuleOrder,
      moduleOrder
    });
    return response.data;
  },
  getChapterProgress: async (chapterId: string) => {
    const response = await api.get(`/progress/chapter/${chapterId}`);
    return response.data;
  },
  getCourseProgress: async (courseId: string): Promise<{
    progress: unknown[];
    byChapter: Record<
      string,
      {
        isCompleted: boolean;
        isUnlocked: boolean;
        completedItems: unknown[];
        completedModules: unknown[];
        timeSpent: number;
        completionDate?: string;
      }
    >;
  }> => {
    const response = await api.get(`/progress/course/${courseId}`);
    return response.data;
  },
  updateProgress: async (chapterId: string, timeSpent?: number, isCompleted?: boolean) => {
    const response = await api.put(`/progress/chapter/${chapterId}`, {
      timeSpent,
      isCompleted
    });
    await relayScormCompletion('incomplete', `Progress updated for chapter: ${chapterId}`);
    return response.data;
  },
  markChapterComplete: async (chapterId: string) => {
    const response = await api.post(`/progress/chapter/${chapterId}/complete`);
    await relayScormCompletion('incomplete', `Chapter completed: ${chapterId}`);
    return response.data;
  }
};

// Module Time Tracking API functions
export const moduleTimeTrackingAPI = {
  updateTime: async (courseId: string, chapterId: string, moduleType: 'capsule' | 'quiz' | 'workshop', moduleId: string, timeSpent: number) => {
    const response = await api.post('/module-time-tracking', {
      courseId,
      chapterId,
      moduleType,
      moduleId,
      timeSpent
    });
    return response.data;
  },
  getTime: async (courseId: string, chapterId: string, moduleType: 'capsule' | 'quiz' | 'workshop', moduleId: string) => {
    const response = await api.get(`/module-time-tracking/${courseId}/${chapterId}/${moduleType}/${moduleId}`);
    return response.data;
  },
  getChapterTimeTracking: async (chapterId: string) => {
    const response = await api.get(`/module-time-tracking/chapter/${chapterId}`);
    return response.data;
  }
};

export default api;
